import { ackTonOrder, claimFreeOffer, createTelegramStarsInvoice, createTonInvoice, getOffers, getTelegramStarsOrder, getTonOrder, getTonOrderTransaction, markTonOrderSent, validateOfferPurchase } from '../api/requests/offers.requests.js';
import { StatisticsStorage } from '../storage/statistics_storage.js';
import type { RewardedShowResult } from '../ads/rewarded.js';
import { OfferEventType, OfferStore } from '../api/dto/offers.dto.js';
import type { ClaimFreeOfferResponse, EntitlementDto, OfferDto, OfferProductDto, OfferProductsDto, OffersResponse, ResolvedOfferPlacementDto, TelegramStarsOrderResponse, TonAsset, TonOrderDto, TonOrderResponse, TonSendTransactionRequest, ValidatePurchaseRequest, ValidatePurchaseResponse } from '../api/dto/offers.dto.js';
import { IapPlugin } from '../plugins/iap_plugin.js';
import { TelegramStars } from '../plugins/telegram_stars.js';
import { classify as classifyTonError, TonConnectFailure, TonPaymentPending, TonPayments } from '../plugins/ton_payments.js';
import { adoptEconomyFromPlayer } from '../api/player_sync.js';
import { TonPaymentSheet } from '../plugins/ton_payment_sheet.js';
import {
  buildOfferPriceAnalyticsProperties,
  trackAppsFlyerPurchase,
  trackOfferAnalytics,
} from './offer_analytics.js';
import { ApiError, NetworkError } from '../api/http_client.js';
import { GlobalSettings, RuntimeEnvironmentKey } from '../global_settings.js';
import { GIT_BRANCH } from '../generated/git_branch.js';
import { CustomEventsController } from '../socket_handler/controllers/custom_events_controller.js';

const NO_ADS_ENTITLEMENT = 'no_ads';
const LEGACY_OFFERS_CACHE_KEY = 'eropix_offers_cache';
const PERSISTENT_ENTITLEMENTS_KEY = 'royal_smash_offer_entitlements';

export type MonetizationSegment = 'free' | 'paid';

let offers_cache: OfferDto[] = [];
let placements_cache: Record<string, ResolvedOfferPlacementDto> = {};
let entitlements_cache: Record<string, boolean> = {};
let refresh_promise: Promise<OffersResponse | null> | null = null;
let server_clock_offset_ms: number | null = null;
// Compatibility fallback for a backend that has the duration field but has
// not yet been updated to return the corresponding server deadline. It stays
// memory-only, so it has the same lifetime as a client session and refreshes
// cannot extend it.
const session_offer_display_deadlines_ms = new Map<string, number>();
// Intentionally memory-only: a new app/browser session gets a fresh server
// window, while refreshes inside it cannot reset a timed offer.
const offer_session_id = typeof globalThis.crypto?.randomUUID === 'function'
  ? globalThis.crypto.randomUUID()
  : `offer-${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}`;
// Debug-only runtime override. It intentionally is neither persisted nor sent to the API/analytics.
let no_ads_cheat_override: boolean | null = null;
// Unlike ENV, this is generated at build time and cannot be changed by a
// runtime localStorage override. A production binary never gets this cheat.
const PURCHASE_SIMULATION_BUILD_ALLOWED = ![ RuntimeEnvironmentKey.PROD, 'production' ].includes( String( GIT_BRANCH ) );
// Debug-only and memory-only. A simulated purchase is still validated and
// granted by the server, but never opens the native store and is persisted as
// a sandbox/test purchase. Production builds cannot enable it.
let simulate_purchase_cheat_enabled = false;
const SIMULATED_PURCHASE_CURRENCY = 'EUR';
const SIMULATED_PURCHASE_PRICE_MICROS = 4_490_000;
// Retained until the server confirms the result, so a lost HTTP response
// retries the same idempotent free claim instead of issuing a second claim.
const pending_free_claim_ids = new Map<string, string>();

function createSimulatedPurchase( product_id: string ): ValidatePurchaseRequest
{
  const transaction_id = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}`;

  return {
    store: OfferStore.Sandbox,
    product_id,
    transaction_id: `cheat-test-${transaction_id}`,
    environment: 'sandbox',
    currency: SIMULATED_PURCHASE_CURRENCY,
    price_micros: SIMULATED_PURCHASE_PRICE_MICROS,
    ...( IapPlugin.getStore() === OfferStore.GooglePlay ? { sandbox_platform: 'android' as const } : {} ),
    ...( IapPlugin.getStore() === OfferStore.AppStore ? { sandbox_platform: 'ios' as const } : {} ),
  };
}

function isEntitlementActive( entitlement: boolean | EntitlementDto | undefined ): boolean
{
  if ( entitlement === true ) return true;
  if ( entitlement && typeof entitlement === 'object' ) return entitlement.active === true;
  return false;
}

function readPositiveDurationSeconds( value: unknown ): number | null
{
  const duration = typeof value === 'number' ? value : Number( value );
  return Number.isInteger( duration ) && duration > 0 ? duration : null;
}

function getOfferDurationFallbackSeconds( offer: OfferDto ): number | null
{
  const durations = [
    offer.shown_duration_seconds,
    offer.first_shown_duration_seconds,
    offer.data?.shown_duration_seconds,
    offer.data?.shownDurationSeconds,
    offer.data?.first_shown_duration_seconds,
    offer.data?.firstShownDurationSeconds,
  ]
    .map( readPositiveDurationSeconds )
    .filter( ( duration ): duration is number => duration !== null );
  return durations.length ? Math.min( ...durations ) : null;
}

function normalizeEntitlements( entitlements: OffersResponse['entitlements'] | null | undefined ): Record<string, boolean>
{
  const normalized: Record<string, boolean> = {};
  if ( !entitlements ) return normalized;

  for ( const [ key, entitlement ] of Object.entries( entitlements ) )
    normalized[key] = isEntitlementActive( entitlement );

  return normalized;
}

function readPersistentEntitlements(): Record<string, boolean>
{
  try
  {
    const raw = localStorage.getItem( PERSISTENT_ENTITLEMENTS_KEY );
    if ( !raw ) return {};

    const parsed = JSON.parse( raw ) as Record<string, unknown>;
    const entitlements: Record<string, boolean> = {};
    for ( const [ key, value ] of Object.entries( parsed ) )
    {
      if ( value === true )
        entitlements[key] = true;
    }

    return entitlements;
  }
  catch
  {
    return {};
  }
}

function persistEntitlement( key: string ): void
{
  try
  {
    localStorage.setItem( PERSISTENT_ENTITLEMENTS_KEY, JSON.stringify( {
      ...readPersistentEntitlements(),
      [key]: true,
    } ) );
  }
  catch {}
}

function clearPersistentEntitlements(): void
{
  try { localStorage.removeItem( PERSISTENT_ENTITLEMENTS_KEY ); }
  catch {}
}

function clearLegacyPersistentCache(): void
{
  try { localStorage.removeItem( LEGACY_OFFERS_CACHE_KEY ); }
  catch {}
}

function getMonetizationSegment(): MonetizationSegment
{
  return Object.values( entitlements_cache ).some( active => active === true ) ? 'paid' : 'free';
}

function emitEntitlementsChanged( include_monetization_segment: boolean = true ): void
{
  try
  {
    window.dispatchEvent( new CustomEvent( 'offersEntitlementsChanged', {
      detail: {
        entitlements: { ...entitlements_cache },
        ...( include_monetization_segment ? { monetization_segment: getMonetizationSegment() } : {} ),
      },
    } ) );
  }
  catch {}
}

function emitPlacementsChanged(): void
{
  try { window.dispatchEvent( new CustomEvent( 'offersPlacementsChanged' ) ); }
  catch {}
}

/**
 * A purchase/claim response carries the offer's new cycle state so the shop
 * can redraw the card without another catalog round trip.
 */
function purchaseLimitAnalytics( result: { purchases_left?: number; limit_resets_at?: string } ): Record<string, unknown>
{
  return {
    ...( result.purchases_left !== undefined ? { purchases_left: result.purchases_left } : {} ),
    ...( result.limit_resets_at ? { limit_resets_at: result.limit_resets_at } : {} ),
  };
}

function applyPurchaseLimitResult( offer_id: string, result: { purchases_left?: number; limit_resets_at?: string } ): void
{
  if ( result.purchases_left === undefined && result.limit_resets_at === undefined ) return;
  offers_cache = offers_cache.map( item => item.id === offer_id
    ? { ...item, purchases_left: result.purchases_left, limit_resets_at: result.limit_resets_at }
    : item );
}

function applyOffersResponse( response: OffersResponse ): void
{
  const server_time_ms = typeof response.server_time === 'string' ? Date.parse( response.server_time ) : NaN;
  if ( Number.isFinite( server_time_ms ) ) server_clock_offset_ms = server_time_ms - Date.now();
  offers_cache = Array.isArray( response.offers ) ? response.offers : [];
  placements_cache = Object.fromEntries( Object.entries( response.placements ?? {} ).filter( ( [ id, placement ] ) =>
    Boolean( id ) && Boolean( placement?.offer_id ) && Boolean( placement?.template )
  ) );
  entitlements_cache = {
    ...normalizeEntitlements( response.entitlements ),
    ...readPersistentEntitlements(),
  };

  rememberTelegramPrices( offers_cache );
  rememberTonPrices( offers_cache );
  resumeTonOrders( response.ton_orders ?? [] );

  for ( const offer of offers_cache )
  {
    const has_server_deadline = [
      offer.session_ends_at,
      offer.first_shown_ends_at,
      offer.ends_at,
      offer.data?.session_ends_at,
      offer.data?.first_shown_ends_at,
      offer.data?.ends_at,
      offer.data?.endsAt,
    ].some( value => typeof value === 'string' && Number.isFinite( Date.parse( value ) ) );
    const duration_fallback_seconds = getOfferDurationFallbackSeconds( offer );
    if ( !has_server_deadline && duration_fallback_seconds !== null
      && !session_offer_display_deadlines_ms.has( offer.id ) )
    {
      session_offer_display_deadlines_ms.set( offer.id, Date.now() + duration_fallback_seconds * 1000 );
    }

    if ( offer.owned )
      entitlements_cache[offer.entitlement_key] = true;
  }

  emitEntitlementsChanged();
  emitPlacementsChanged();
}

export function getPlatformProductConfig( products: OfferProductsDto, store: OfferStore | null = IapPlugin.getStore() ): OfferProductDto | null
{
  if ( store === OfferStore.GooglePlay ) return products.android ?? null;
  if ( store === OfferStore.AppStore ) return products.ios ?? null;
  if ( store === OfferStore.TelegramStars ) return products.telegram ?? null;
  if ( store === OfferStore.Ton ) return products.ton ?? null;
  return products.android ?? products.ios ?? null;
}

function rememberTonPrices( offers: OfferDto[] ): void
{
  // A fresh list replaces the old prices: a product that lost its crypto price (channel gate, kill-switch) stops offering it.
  TonPayments.clearPrices();
  for ( const offer of offers )
  {
    const all_products = [ offer.products, ...( offer.chain?.steps.map( step => step.products ?? {} ) ?? [] ) ];
    for ( const products of all_products )
    {
      const ton = products?.ton;
      if ( ton?.productId ) TonPayments.rememberPrices( ton.productId, { ton: ton.priceTon, usdt: ton.priceUsdt, usd: ton.priceUsd } );
    }
  }
}

function rememberTelegramPrices( offers: OfferDto[] ): void
{
  for ( const offer of offers )
  {
    const all_products = [ offer.products, ...( offer.chain?.steps.map( step => step.products ?? {} ) ?? [] ) ];
    for ( const products of all_products )
    {
      const telegram = products?.telegram;
      if ( telegram?.productId && typeof telegram.priceStars === 'number' ) TelegramStars.rememberPrice( telegram.productId, telegram.priceStars );
    }
  }
}

// ---------------------------------------------------------------------------
// TON / USDT: the chain confirms the payment; the client only polls.
// ---------------------------------------------------------------------------

const TON_POLL_MAX_MS = 20 * 60_000;
const TON_POLL_ERROR_MAX_MS = 60_000;
// Orders this session already tracks in the background (resume after a reload or a wallet round-trip).
const ton_trackers = new Set<string>();
// Orders acknowledged in this session: the foreground flow, the tracker and a refresh may all finish the same order.
const ton_acked = new Set<string>();

function ackTonOrderOnce( order_id: string ): Promise<void>
{
  if ( ton_acked.has( order_id ) ) return Promise.resolve();
  ton_acked.add( order_id );
  return ackTonOrder( order_id ).then( () => undefined ).catch( () => { ton_acked.delete( order_id ); } );
}

/** Resolves when the tab is visible again (polling pauses in the background: the server keeps the order either way). */
function whenVisible(): Promise<void>
{
  if ( typeof document === 'undefined' || !document.hidden ) return Promise.resolve();
  return new Promise( resolve =>
  {
    const onChange = () => { if ( !document.hidden ) { document.removeEventListener( 'visibilitychange', onChange ); resolve(); } };
    document.addEventListener( 'visibilitychange', onChange );
  } );
}

function isTonTerminal( status: string ): boolean
{
  return status === 'fulfilled' || status === 'failed' || status === 'refunding' || status === 'refunded';
}

function emitTonOrderChanged( order: TonOrderResponse ): void
{
  try { window.dispatchEvent( new CustomEvent( 'tonOrderChanged', { detail: order } ) ); }
  catch { /* no window in tests */ }
}

/**
 * Polls a TON order until the server has granted it or it can no longer be.
 * Server-side 5xx and network blips are waited out, never treated as failure.
 * `onUpdate` receives every poll so the sheet can show what the chain says.
 */
async function waitForTonOrder( order_id: string, options: { onUpdate?: ( order: TonOrderResponse ) => Promise<'continue' | 'stop'> | 'continue' | 'stop'; max_ms?: number } = {} ): Promise<TonOrderResponse>
{
  const deadline = Date.now() + ( options.max_ms ?? TON_POLL_MAX_MS );
  let delay = 2000;
  let error_delay = 2000;
  while ( Date.now() < deadline )
  {
    await whenVisible();
    try
    {
      const order = await getTonOrder( order_id );
      delay = Math.max( 1000, Math.min( 15_000, Number( order.poll_after_ms ) || 2000 ) );
      error_delay = delay;
      if ( order.status === 'fulfilled' ) return order;
      if ( order.status === 'failed' || order.status === 'refunding' || order.status === 'refunded' ) throw new Error( `ton_order_${order.status}` );
      if ( order.status === 'expired' && !order.late_possible )
      {
        // Expired with money on it (a partial payment, a wrong asset): never "nothing was charged".
        const money = Number( order.paid_amount ) > 0 || ( order.extra_payments?.length ?? 0 ) > 0;
        throw new Error( money ? 'ton_order_expired_paid' : 'ton_order_expired' );
      }
      if ( options.onUpdate && ( await options.onUpdate( order ) ) === 'stop' ) throw new TonPaymentPending( order_id );
    }
    catch ( e )
    {
      // Outages back off exponentially; the server keeps the order either way.
      if ( e instanceof NetworkError || ( e instanceof ApiError && ( e.status >= 500 || e.status === 429 ) ) )
      {
        error_delay = Math.min( TON_POLL_ERROR_MAX_MS, error_delay * 2 );
        await sleep( error_delay );
        continue;
      }
      throw e;
    }
    await sleep( delay );
  }
  throw new TonPaymentPending( order_id );
}

/** Keeps polling an order after the player left the sheet; delivers the result through OfferService.refresh and an event. */
function trackTonOrderInBackground( order_id: string ): void
{
  if ( ton_trackers.has( order_id ) ) return;
  ton_trackers.add( order_id );
  void ( async () =>
  {
    try
    {
      const order = await waitForTonOrder( order_id, { max_ms: 25 * 60 * 60_000 } );
      // Acknowledge before the refresh: the refresh re-reads ton_orders and would otherwise ack and emit it again.
      await ackTonOrderOnce( order_id );
      if ( order.player ) adoptEconomyFromPlayer( order.player );
      await OfferService.refresh();
      emitTonOrderChanged( order );
    }
    catch ( e )
    {
      const reason = e instanceof Error ? e.message : String( e );
      console.warn( '[Offers] TON order tracking ended', order_id, reason );
      // Money is on the order but nothing was granted: tell the player once (nobody listens to the event),
      // then acknowledge so GET /offers stops handing the order back on every refresh.
      if ( ( reason === 'ton_order_expired_paid' || reason === 'ton_order_failed' ) && !TonPaymentSheet.isOpen() )
      {
        TonPaymentSheet.show( { kind: 'needs_support', order_id } );
      }
      if ( /^ton_order_(expired|expired_paid|failed|refunded)$/.test( reason ) ) await ackTonOrderOnce( order_id );
    }
    finally
    {
      ton_trackers.delete( order_id );
    }
  } )();
}

/** Open orders reported by GET /offers: resume them without any local storage. */
function resumeTonOrders( orders: NonNullable<OffersResponse['ton_orders']> ): void
{
  for ( const summary of orders )
  {
    if ( summary.status === 'fulfilled' && !summary.acknowledged )
    {
      if ( ton_acked.has( summary.order_id ) ) continue;
      void getTonOrder( summary.order_id ).then( ( order ) =>
      {
        if ( order.player ) adoptEconomyFromPlayer( order.player );
        emitTonOrderChanged( order );
        return ackTonOrderOnce( summary.order_id );
      } ).catch( () => {} );
      continue;
    }
    if ( summary.status === 'paid' || summary.status === 'underpaid' || summary.status === 'failed' || ( summary.status === 'pending' && summary.stage !== 'awaiting_payment' ) || ( summary.status === 'expired' && summary.stage !== 'awaiting_payment' ) )
    {
      trackTonOrderInBackground( summary.order_id );
    }
  }
}

function tonSheetForOrder( order: TonOrderDto ): void
{
  if ( !TonPaymentSheet.isOpen() ) return;
  if ( order.status === 'underpaid' ) TonPaymentSheet.show( { kind: 'underpaid', order_id: order.order_id, received: TonPayments.formatAsset( order.asset, Number( order.paid_amount ) / ( order.asset === 'TON' ? 1e9 : 1e6 ) ), amount: order.amount_display } );
  else if ( order.stage === 'granting' ) TonPaymentSheet.show( { kind: 'granting' } );
  else if ( order.stage === 'payment_seen' ) TonPaymentSheet.show( { kind: 'payment_seen', explorer_url: order.explorer_url } );
}

const STARS_POLL_INTERVAL_MS = 1500;
// Telegram usually delivers successful_payment within seconds. Past this the
// server still grants the order on its own; the player sees it on next refresh.
const STARS_POLL_TIMEOUT_MS = 60_000;

function sleep( ms: number ): Promise<void>
{
  return new Promise( resolve => setTimeout( resolve, ms ) );
}

/** Polls a Stars order until the server has granted it, or it can no longer be. */
async function waitForStarsOrder( order_id: string ): Promise<TelegramStarsOrderResponse>
{
  const deadline = Date.now() + STARS_POLL_TIMEOUT_MS;
  let last_network_error: unknown = null;
  while ( Date.now() < deadline )
  {
    try
    {
      const order = await getTelegramStarsOrder( order_id );
      if ( order.status === 'fulfilled' ) return order;
      if ( order.status === 'refunding' || order.status === 'refunded' ) throw new Error( 'telegram_stars_payment_refunded' );
      if ( order.status === 'expired' || order.status === 'failed' ) throw new Error( `telegram_stars_order_${order.status}` );
      last_network_error = null;
    }
    catch ( e )
    {
      if ( !( e instanceof NetworkError ) ) throw e;
      last_network_error = e;
    }
    await sleep( STARS_POLL_INTERVAL_MS );
  }
  throw last_network_error ?? new Error( 'telegram_stars_payment_processing' );
}

function getPlatformProduct( offer: OfferDto, store?: OfferStore ): string | null
{
  return getPlatformProductConfig( offer.products, store ?? IapPlugin.getStore() )?.productId ?? null;
}

type RestoredOfferMatch = {
  offer: OfferDto;
  product: OfferProductDto;
  chain_step_id?: string;
};

/** A chain owns its store SKUs on individual cards, not on the parent offer. */
function findRestoredOfferMatch( product_id: string ): RestoredOfferMatch | null
{
  for ( const offer of offers_cache )
  {
    const product = Object.values( offer.products ).find( item => item?.productId === product_id );
    if ( product ) return { offer, product };

    const matching_steps = offer.chain?.steps.filter( item =>
      Object.values( item.products ?? {} ).some( product => product?.productId === product_id )
    ) ?? [];
    // A chain is allowed to reuse an SKU. During restore, the server-issued
    // cursor identifies which matching card is awaiting validation.
    const current_step_id = offer.chain?.next_step_id ?? offer.chain?.next_paid_step_id;
    const step = matching_steps.find( item => item.id === current_step_id ) ?? matching_steps[0];
    const chain_product = step && Object.values( step.products ?? {} ).find( product => product?.productId === product_id );
    if ( step && chain_product ) return { offer, product: chain_product, chain_step_id: step.id };
  }
  return null;
}

function isFreeOffer( offer: OfferDto ): boolean
{
  return offer.purchase?.type === 'free';
}

function isRewardedOffer( offer: OfferDto ): boolean
{
  return offer.purchase?.type === 'rewarded';
}

function getPlatformPurchaseMetadata( offer: OfferDto, product_override?: OfferProductDto ): Partial<ValidatePurchaseRequest>
{
  const product = product_override ?? getPlatformProductConfig( offer.products );

  return {
    ...( product?.packageName ? { package_name: product.packageName } : {} ),
    ...( product?.bundleId ? { bundle_id: product.bundleId } : {} ),
    ...( product?.environment ? { environment: product.environment } : {} ),
  };
}

function withOfferPurchaseMetadata( offer: OfferDto, purchase: ValidatePurchaseRequest, product_override?: OfferProductDto, chain_step_id?: string, trigger_event_id?: string, placement_id?: string ): ValidatePurchaseRequest
{
  return {
    session_id: offer_session_id,
    ...getPlatformPurchaseMetadata( offer, product_override ),
    ...( chain_step_id ? { chain_step_id } : {} ),
    ...( trigger_event_id ? { trigger_event_id } : {} ),
    ...( placement_id ? { placement_id } : {} ),
    ...purchase,
  };
}

function getPurchaseFailureProperties( error: unknown, stage: 'store' | 'validation' | 'claim' ): Record<string, unknown>
{
  const raw = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const message = error instanceof Error ? error.message : String( error ?? 'unknown_error' );
  const native_code = typeof raw.code === 'string' || typeof raw.code === 'number' ? String( raw.code ) : undefined;
  const normalized_message = message.toLowerCase();

  let reason = 'unknown';
  if ( error instanceof TonConnectFailure ) reason = error.reason;
  else if ( normalized_message === 'ton_payment_processing' ) reason = 'payment_pending';
  else if ( normalized_message === 'ton_order_expired' ) reason = 'order_expired';
  else if ( normalized_message.startsWith( 'ton_order_' ) ) reason = normalized_message;
  else if ( normalized_message.includes( 'cancel' ) ) reason = 'cancelled_by_user';
  else if ( normalized_message.includes( 'already_in_progress' ) ) reason = 'purchase_already_in_progress';
  else if ( normalized_message.includes( 'product_not_found' ) ) reason = 'product_not_found';
  else if ( normalized_message.includes( 'billing_setup_failed' ) || normalized_message.includes( 'billing_client_unavailable' ) ) reason = 'billing_unavailable';
  else if ( normalized_message.includes( 'query_product_details_failed' ) ) reason = 'product_details_unavailable';
  else if ( normalized_message.includes( 'launch_billing_flow_failed' ) ) reason = 'billing_flow_not_started';
  else if ( normalized_message.includes( 'purchase_not_completed' ) ) reason = 'purchase_not_completed';
  else if ( normalized_message.includes( 'purchase_result_missing' ) ) reason = 'purchase_result_missing';
  else if ( error instanceof NetworkError ) reason = 'network_error';
  else if ( error instanceof ApiError ) reason = `validation_http_${error.status}`;

  return {
    purchase_stage: stage,
    failure_reason: reason,
    ...( native_code ? { failure_code: native_code } : {} ),
    ...( error instanceof ApiError ? { http_status: error.status } : {} ),
    error_message: message.slice( 0, 500 ),
  };
}

function isGooglePlayItemAlreadyOwned( error: unknown ): boolean
{
  const raw = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const code = typeof raw.code === 'string' || typeof raw.code === 'number' ? String( raw.code ) : '';
  const message = error instanceof Error ? error.message : String( error ?? '' );
  const normalized_message = message.toLowerCase();

  return code === '7' ||
    normalized_message.includes( 'item_already_owned' ) ||
    normalized_message.includes( 'item already owned' ) ||
    normalized_message.includes( 'already owned' );
}

function canReplaceOwnedGooglePlayPurchaseAfterValidationFailure( error: unknown ): boolean
{
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

async function consumeValidatedGooglePlayPurchase(
  purchase: Pick<ValidatePurchaseResponse['purchase'], 'store'> & { purchase_token?: string },
): Promise<'consumed' | 'not_consumable' | 'failed'>
{
  if ( purchase.store !== OfferStore.GooglePlay || !purchase.purchase_token ) return 'not_consumable';

  try
  {
    const result = await IapPlugin.consumePurchase( {
      store:          purchase.store,
      purchase_token: purchase.purchase_token,
    } );
    return result?.ok ? 'consumed' : 'not_consumable';
  }
  catch ( e )
  {
    console.warn( '[Offers] Google Play consume failed after validation', e );
    return 'failed';
  }
}

function shouldConsumeGooglePlayPurchase( offer: OfferDto, server_purchase_type?: string | null ): boolean
{
  if ( server_purchase_type === 'non_consumable' || server_purchase_type === 'non-consumable' ) return false;
  if ( server_purchase_type === 'consumable' ) return true;
  // The server schema owns this flag. Keep the entitlement fallback for older
  // configs during rollout, but never consume a non-consumable product.
  const configured = readConfiguredPurchaseType( offer )?.toLowerCase();
  if ( configured === 'non_consumable' || configured === 'non-consumable' ) return false;
  return offer.entitlement_key !== NO_ADS_ENTITLEMENT;
}

function canRecoverOwnedGooglePlayPurchase( offer: OfferDto, chain_step_id?: string ): boolean
{
  const chain_step = chain_step_id ? offer.chain?.steps.find( step => step.id === chain_step_id ) : undefined;
  // A permanent chain card must be acknowledged, never consumed. A stale UI
  // can still reach this path before the refreshed eligibility hides it.
  if ( chain_step?.rewards.no_ads === true ) return false;
  return shouldConsumeGooglePlayPurchase( offer );
}

type PurchaseAnalyticsMetadata = {
  store: OfferStore;
  purchase_type: string;
  is_test: boolean;
  transaction_id?: string;
  /** Analytics pricing contract: what the player paid in USD, what the SKU was meant to cost, and the chain position. */
  priceusd?: number | null;
  originalusd?: number | null;
  revenue_usd?: number | null;
  offer_step?: number;
  price_source?: string;
};

/**
 * The USD/step properties that are known before any store call: the
 * configured price and the chain position. The paid USD amount only exists
 * once the server has confirmed the receipt.
 */
function offerPricingAnalytics( offer: OfferDto, chain_step_id?: string ): Pick<PurchaseAnalyticsMetadata, 'originalusd' | 'priceusd' | 'offer_step'>
{
  if ( offer.chain && chain_step_id )
  {
    const index = offer.chain.steps.findIndex( step => step.id === chain_step_id );
    const step = index >= 0 ? offer.chain.steps[index] : undefined;
    return {
      ...( step?.price_usd !== undefined ? { originalusd: step.price_usd, priceusd: step.price_usd } : {} ),
      ...( index >= 0 ? { offer_step: index + 1 } : {} ),
    };
  }
  return offer.price_usd !== undefined ? { originalusd: offer.price_usd, priceusd: offer.price_usd } : {};
}

/** Per-product USD price when the server attaches it to the SKU itself (legacy offers without a catalog product). */
function readStorePriceUsd( offer: OfferDto, product_override?: OfferProductDto, store?: OfferStore ): number | undefined
{
  const product = product_override ?? getPlatformProductConfig( offer.products, store ?? IapPlugin.getStore() );
  const raw = product?.priceUsd;
  return typeof raw === 'number' && Number.isFinite( raw ) && raw >= 0 ? raw : undefined;
}

function readFiniteNumber( value: unknown ): number | undefined
{
  return typeof value === 'number' && Number.isFinite( value ) ? value : undefined;
}

function readConfiguredPurchaseType( offer: OfferDto ): string | null
{
  const raw = offer.data?.purchase_type ?? offer.data?.purchaseType;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function buildPurchaseAnalyticsMetadata(
  offer: OfferDto,
  purchase: ValidatePurchaseRequest,
  validation?: ValidatePurchaseResponse | null,
  product_override?: OfferProductDto,
): PurchaseAnalyticsMetadata
{
  const validated_purchase = validation?.purchase;
  const store_price_usd = readStorePriceUsd( offer, product_override );
  const original_usd = readFiniteNumber( validated_purchase?.original_usd ) ?? readFiniteNumber( validated_purchase?.original_price_usd );
  const price_source = typeof validated_purchase?.price_source === 'string' ? validated_purchase.price_source : undefined;
  const store = validated_purchase?.store ?? purchase.store;
  const server_purchase_type = validated_purchase?.purchase_type;
  const purchase_type = typeof server_purchase_type === 'string' && server_purchase_type.trim()
    ? server_purchase_type.trim()
    : readConfiguredPurchaseType( offer )
      ?? ( shouldConsumeGooglePlayPurchase( offer ) ? 'consumable' : 'non_consumable' );
  const server_transaction_id = validated_purchase?.transaction_id;
  const transaction_id = typeof server_transaction_id === 'string' && server_transaction_id.trim()
    ? server_transaction_id.trim()
    : purchase.transaction_id;
  const is_test = !GlobalSettings.IS_PROD
    || validated_purchase?.is_test === true
    || validated_purchase?.environment === 'sandbox'
    || purchase.environment === 'sandbox'
    || store === OfferStore.Sandbox;

  return {
    store,
    purchase_type,
    is_test,
    ...( transaction_id ? { transaction_id } : {} ),
    ...offerPricingAnalytics( offer, typeof purchase.chain_step_id === 'string' ? purchase.chain_step_id : undefined ),
    ...( store_price_usd !== undefined ? { originalusd: store_price_usd } : {} ),
    ...( validated_purchase?.price_usd !== undefined ? { priceusd: validated_purchase.price_usd } : {} ),
    ...( original_usd !== undefined ? { originalusd: original_usd } : {} ),
    ...( validated_purchase?.revenue_usd !== undefined ? { revenue_usd: validated_purchase.revenue_usd } : {} ),
    ...( validated_purchase?.offer_step !== undefined ? { offer_step: validated_purchase.offer_step } : {} ),
    ...( price_source ? { price_source } : {} ),
  };
}

async function finishValidatedGooglePlayPurchase(
  offer: OfferDto,
  purchase: Pick<ValidatePurchaseResponse['purchase'], 'store' | 'purchase_type'> & { purchase_token?: string },
): Promise<'consumed' | 'acknowledged' | 'not_required' | 'failed'>
{
  if ( purchase.store !== OfferStore.GooglePlay || !purchase.purchase_token ) return 'not_required';

  if ( shouldConsumeGooglePlayPurchase( offer, purchase.purchase_type ) )
  {
    const result = await consumeValidatedGooglePlayPurchase( purchase );
    return result === 'not_consumable' ? 'not_required' : result;
  }

  try
  {
    const result = await IapPlugin.acknowledgePurchase( {
      store:          purchase.store,
      purchase_token: purchase.purchase_token,
    } );
    return result?.ok ? 'acknowledged' : 'not_required';
  }
  catch ( e )
  {
    console.warn( '[Offers] Google Play acknowledge failed after validation', e );
    return 'failed';
  }
}

clearLegacyPersistentCache();

export const OfferService = {
  async refresh(): Promise<OffersResponse | null>
  {
    if ( refresh_promise ) return refresh_promise;
    refresh_promise = ( async () =>
    {
      try
      {
        const response = await getOffers( offer_session_id );
        applyOffersResponse( response );
        return response;
      }
      catch ( e )
      {
        console.warn( '[Offers] refresh failed', e );
        // Manual entry points are server-authoritative. Do not keep an old
        // placement button alive when the latest availability cannot load.
        placements_cache = {};
        emitPlacementsChanged();
        return null;
      }
      finally
      {
        refresh_promise = null;
      }
    } )();

    return refresh_promise;
  },

  getOffers(): OfferDto[]
  {
    return offers_cache;
  },

  /**
   * The backend normally supplies an absolute deadline. A temporary
   * compatibility fallback keeps a timed offer visible on older backends that
   * expose a duration only in their legacy data payload.
   */
  getSessionOfferDisplayDeadlineMs( offer: OfferDto ): number | null
  {
    return session_offer_display_deadlines_ms.get( offer.id ) ?? null;
  },

  /**
   * The deadline a timed offer counts down to. The backend spells the field
   * differently across payload shapes, and older ones expose a duration only,
   * so every screen showing a timer must resolve it the same way.
   */
  getOfferDisplayEndMs( offer: OfferDto ): number | null
  {
    const deadlines = [
      offer.session_ends_at,
      offer.first_shown_ends_at,
      offer.ends_at,
      offer.data?.session_ends_at,
      offer.data?.first_shown_ends_at,
      offer.data?.ends_at,
      offer.data?.endsAt,
    ]
      .filter( ( value ): value is string => typeof value === 'string' )
      .map( value => Date.parse( value ) )
      .filter( Number.isFinite );

    return deadlines.length ? Math.min( ...deadlines ) : this.getSessionOfferDisplayDeadlineMs( offer );
  },

  /** Returns null until the offers endpoint has supplied a server clock. */
  getServerNowMs(): number | null
  {
    return server_clock_offset_ms === null ? null : Date.now() + server_clock_offset_ms;
  },

  getOffer( offer_id: string ): OfferDto | null
  {
    return offers_cache.find( offer => offer.id === offer_id ) ?? null;
  },

  /** Current cycle of a limited offer, or null when it can be bought freely. */
  getPurchaseLimitState( offer: OfferDto ): { left: number; max: number; resets_at_ms: number | null } | null
  {
    if ( !offer.purchase_limit || offer.purchases_left === undefined ) return null;
    const resets_at_ms = offer.limit_resets_at ? Date.parse( offer.limit_resets_at ) : NaN;
    return {
      left: Math.max( 0, offer.purchases_left ),
      max: offer.purchase_limit.max,
      resets_at_ms: Number.isFinite( resets_at_ms ) ? resets_at_ms : null,
    };
  },

  /** Returns the exact server-authorized offer for a manual UI entry point. */
  getPlacementOffer( placement_id: string ): { offer: OfferDto; template: string } | null
  {
    if ( !GlobalSettings.PROMO_OFFERS_ENABLED ) return null;
    const placement = placements_cache[placement_id];
    if ( !placement ) return null;
    const offer = this.getOffer( placement.offer_id );
    return offer?.presentation === 'placement_dialog'
      ? { offer, template: placement.template }
      : null;
  },

  /** Trigger offers arrive from a server decision rather than GET /offers. */
  rememberOffer( offer: OfferDto ): void
  {
    offers_cache = [ ...offers_cache.filter( item => item.id !== offer.id ), offer ];
  },

  getPlatformProductId( offer_id: string ): string | null
  {
    const offer = this.getOffer( offer_id );
    return offer ? getPlatformProduct( offer ) : null;
  },

  hasEntitlement( key: string ): boolean
  {
    return entitlements_cache[key] === true
      || readPersistentEntitlements()[key] === true
      || offers_cache.some( offer => offer.entitlement_key === key && offer.owned === true );
  },

  hasNoAds(): boolean
  {
    if ( no_ads_cheat_override != null ) return no_ads_cheat_override;
    return this.hasEntitlement( NO_ADS_ENTITLEMENT );
  },

  /** Owns the permanent entitlement, or is inside a timed no_ads reward window. */
  hasNoInterstitialAds(): boolean
  {
    return this.hasNoAds() || StatisticsStorage.load().no_ads_until > Date.now();
  },

  getNoAdsCheatOverride(): boolean | null
  {
    return no_ads_cheat_override;
  },

  setNoAdsCheatOverride( no_ads: boolean | null ): void
  {
    no_ads_cheat_override = no_ads;
    // A debug override changes only ad behaviour. It must not update analytics identity.
    emitEntitlementsChanged( false );
  },

  isPurchaseSimulationAvailable(): boolean
  {
    return PURCHASE_SIMULATION_BUILD_ALLOWED && !GlobalSettings.IS_PROD;
  },

  isPurchaseSimulationEnabled(): boolean
  {
    return this.isPurchaseSimulationAvailable() && simulate_purchase_cheat_enabled;
  },

  setPurchaseSimulationEnabled( enabled: boolean ): void
  {
    // Keep this as a non-production-only guard in the service too: the debug
    // UI is not the security boundary, and a production build must never
    // substitute a real store purchase with a sandbox one.
    simulate_purchase_cheat_enabled = this.isPurchaseSimulationAvailable() && enabled;
  },

  getMonetizationSegment(): MonetizationSegment
  {
    return getMonetizationSegment();
  },

  clearCachedState(): void
  {
    offers_cache = [];
    pending_free_claim_ids.clear();
    entitlements_cache = {};
    server_clock_offset_ms = null;
    clearLegacyPersistentCache();
    clearPersistentEntitlements();
    emitEntitlementsChanged();
  },

  async trackShown( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Shown, extra );
  },

  async trackPlacementImpression( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PlacementImpression, extra );
  },

  async trackClicked( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Clicked, extra );
  },

  async trackClosed( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.Close, extra );
  },

  async trackPriceLoaded( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PriceLoaded, extra );
  },

  async trackPriceUnavailable( offer_id: string, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( offer ) await trackOfferAnalytics( offer, OfferEventType.PriceUnavailable, extra );
  },

  /**
   * A rewarded offer's ad is the first purchase stage: an ad that was not
   * watched to the end is a failed purchase with a reason, the same shape the
   * store stage reports, so the funnel reads continuously.
   */
  async trackRewardedAdOutcome( offer_id: string, watched: RewardedShowResult, extra?: Record<string, unknown> ): Promise<void>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer ) return;
    const ad_context = { rewarded: true, ...( watched.ad_id ? { rewarded_ad_id: watched.ad_id } : {} ), ...extra };
    if ( watched.rewarded )
    {
      await trackOfferAnalytics( offer, OfferEventType.Clicked, { ...ad_context, purchase_stage: 'rewarded_ad', rewarded_ad_completed: true } );
      return;
    }
    await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
      ...ad_context,
      purchase_stage: 'rewarded_ad',
      failure_reason: watched.refused ? 'rewarded_ad_refused' : watched.shown ? 'rewarded_ad_not_rewarded' : 'rewarded_ad_unavailable',
    } );
  },

  isFreeOffer( offer_id: string ): boolean
  {
    const offer = this.getOffer( offer_id );
    return Boolean( offer && isFreeOffer( offer ) );
  },

  /** Claims a server-authorized free static or trigger offer. This deliberately
   * bypasses IAP: the server validates the durable per-player claim limit. */
  /** A rewarded offer is claimed through the same call once the ad SDK confirmed the video. */
  isRewardedOffer( offer_id: string ): boolean
  {
    const offer = this.getOffer( offer_id );
    return Boolean( offer && isRewardedOffer( offer ) );
  },

  async claimFreeOffer(
    offer_id: string,
    analytics_context?: Record<string, unknown>,
    rewarded?: { ad_id: string | null },
  ): Promise<ClaimFreeOfferResponse>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer || !( isFreeOffer( offer ) || ( isRewardedOffer( offer ) && rewarded ) ) ) throw new Error( `Free offer not loaded: ${offer_id}` );
    const claim_id = pending_free_claim_ids.get( offer_id ) ?? ( typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `free-claim-${Date.now()}-${Math.random().toString( 36 ).slice( 2 )}` );
    pending_free_claim_ids.set( offer_id, claim_id );
    const trigger_event_id = typeof analytics_context?.trigger_event_id === 'string'
      ? analytics_context.trigger_event_id
      : undefined;
    const placement_id = typeof analytics_context?.placement_id === 'string'
      ? analytics_context.placement_id
      : undefined;
    const context = { free: true, claim_id, priceusd: 0, revenue_usd: 0, ...offerPricingAnalytics( offer ), ...( rewarded ? { rewarded: true, rewarded_ad_id: rewarded.ad_id } : {} ), ...analytics_context };
    await trackOfferAnalytics( offer, OfferEventType.PurchaseStarted, context );
    try
    {
      const result = await claimFreeOffer( offer_id, claim_id, trigger_event_id, offer_session_id, placement_id, rewarded );
      pending_free_claim_ids.delete( offer_id );
      applyPurchaseLimitResult( offer_id, result );
      await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
        ...context,
        grant_applied: result.grant_applied,
        claim_count: result.claim_count,
        ...purchaseLimitAnalytics( result ),
      } );
      await trackOfferAnalytics( offer, OfferEventType.FreeClaim, {
        ...context,
        grant_applied: result.grant_applied,
        claim_count: result.claim_count,
        ...purchaseLimitAnalytics( result ),
      } );
      await this.refresh();
      return result;
    }
    catch ( error )
    {
      await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
        ...context,
        ...getPurchaseFailureProperties( error, 'claim' ),
      } );
      throw error;
    }
  },

  /** `options.store` picks the payment path for this purchase (the shop shows Stars and crypto side by side); default: the build's store. */
  async purchaseOffer(
    offer_id: string,
    analytics_context?: Record<string, unknown>,
    product_override?: OfferProductDto,
    options: { store?: OfferStore } = {},
  ): Promise<ValidatePurchaseResponse>
  {
    const offer = this.getOffer( offer_id );
    if ( !offer ) throw new Error( `Offer not loaded: ${offer_id}` );
    if ( isFreeOffer( offer ) || isRewardedOffer( offer ) ) throw new Error( 'Free and rewarded offers must be claimed without a store purchase' );

    const store = options.store ?? IapPlugin.getStore();
    const product_id = product_override?.productId ?? getPlatformProduct( offer, store ?? undefined );
    if ( !product_id ) throw new Error( `Offer ${offer_id} is not configured for this platform` );

    const is_simulated_purchase = this.isPurchaseSimulationEnabled();
    const store_price_usd = readStorePriceUsd( offer, product_override, store ?? undefined );
    const common_analytics_context = {
      productid: product_id,
      ...( store_price_usd !== undefined ? { originalusd: store_price_usd } : {} ),
      ...( is_simulated_purchase ? { test_purchase: true } : {} ),
      ...analytics_context,
    };
    const chain_step_id = typeof analytics_context?.chain_step_id === 'string' ? analytics_context.chain_step_id : undefined;
    const trigger_event_id = typeof analytics_context?.trigger_event_id === 'string' ? analytics_context.trigger_event_id : undefined;
    const placement_id = typeof analytics_context?.placement_id === 'string' ? analytics_context.placement_id : undefined;
    Object.assign( common_analytics_context, offerPricingAnalytics( offer, chain_step_id ) );
    await trackOfferAnalytics( offer, OfferEventType.PurchaseStarted, common_analytics_context );

    if ( !is_simulated_purchase && store === OfferStore.TelegramStars )
    {
      return this.purchaseWithTelegramStars( offer, product_id, common_analytics_context, chain_step_id, trigger_event_id, placement_id );
    }
    if ( !is_simulated_purchase && store === OfferStore.Ton )
    {
      return this.purchaseWithTon( offer, product_id, common_analytics_context, chain_step_id, trigger_event_id, placement_id );
    }

    let purchase: ValidatePurchaseRequest | undefined = is_simulated_purchase
      ? createSimulatedPurchase( product_id )
      : undefined;
    let validation: ValidatePurchaseResponse | null = null;
    let recovered_owned_product = false;
    let reused_owned_purchase = false;
    if ( !purchase ) try
    {
      purchase = await IapPlugin.purchaseProduct( product_id );
    }
    catch ( e )
    {
      if ( isGooglePlayItemAlreadyOwned( e ) && IapPlugin.getStore() === OfferStore.GooglePlay )
      {
        try
        {
          const restored = await IapPlugin.restorePurchases();
          const owned_purchase = restored.find( item => item.product_id === product_id );

          if ( owned_purchase )
          {
            try
            {
              validation = await validateOfferPurchase( offer.id, withOfferPurchaseMetadata( offer, owned_purchase, product_override, chain_step_id, trigger_event_id, placement_id ) );
              purchase = owned_purchase;
              reused_owned_purchase = true;
            }
            catch ( validation_error )
            {
              if ( !canReplaceOwnedGooglePlayPurchaseAfterValidationFailure( validation_error ) || !canRecoverOwnedGooglePlayPurchase( offer, chain_step_id ) ) throw validation_error;

              const consume_result = await IapPlugin.consumeOwnedProduct( product_id );
              if ( !consume_result?.ok ) throw validation_error;

              recovered_owned_product = consume_result.consumed;
              purchase = await IapPlugin.purchaseProduct( product_id );
            }
          }
          else
          {
            if ( !canRecoverOwnedGooglePlayPurchase( offer, chain_step_id ) ) throw e;
            const consume_result = await IapPlugin.consumeOwnedProduct( product_id );
            if ( !consume_result?.ok ) throw e;

            recovered_owned_product = consume_result.consumed;
            purchase = await IapPlugin.purchaseProduct( product_id );
          }
        }
        catch ( retry_error )
        {
          await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
            ...common_analytics_context,
            ...getPurchaseFailureProperties( retry_error, 'store' ),
            recovered_owned_product,
            initial_failure_reason: 'item_already_owned',
          } );
          throw retry_error;
        }
      }
      else
      {
        await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
          ...common_analytics_context,
          ...getPurchaseFailureProperties( e, 'store' ),
        } );
        throw e;
      }
    }

    if ( !purchase )
    {
      await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
        ...common_analytics_context,
        purchase_stage: 'store',
        failure_reason: 'purchase_result_missing',
        error_message: 'Purchase result missing after Google Play owned-product recovery',
      } );
      throw new Error( 'purchase_result_missing' );
    }

    try
    {
      const validated_purchase = validation ?? await validateOfferPurchase( offer.id, withOfferPurchaseMetadata( offer, purchase, product_override, chain_step_id, trigger_event_id, placement_id ) );
      applyPurchaseLimitResult( offer.id, validated_purchase );
      const purchase_analytics_metadata = buildPurchaseAnalyticsMetadata( offer, purchase, validated_purchase, product_override );
      const consume_status = await finishValidatedGooglePlayPurchase( offer, {
        ...purchase,
        purchase_type: validated_purchase.purchase?.purchase_type,
      } );
      const completed_purchase_context = {
        ...common_analytics_context,
        ...buildOfferPriceAnalyticsProperties( purchase ),
        ...purchase_analytics_metadata,
        ...purchaseLimitAnalytics( validated_purchase ),
      };
      const entitlement = validated_purchase.entitlement;
      if ( entitlement?.active )
      {
        persistEntitlement( entitlement.key );
        entitlements_cache[entitlement.key] = true;
        offers_cache = offers_cache.map( item =>
          item.entitlement_key === entitlement.key ? { ...item, owned: true } : item
        );
        emitEntitlementsChanged();

        await trackAppsFlyerPurchase( offer, {
          productid: purchase.product_id ?? product_id,
          currency: purchase.currency,
          price_micros: purchase.price_micros,
          ...( chain_step_id ? { chain_step_id } : {} ),
          ...( typeof analytics_context?.placement === 'string' ? { placement: analytics_context.placement } : {} ),
          ...( placement_id ? { placement_id } : {} ),
          ...( typeof analytics_context?.template === 'string' ? { template: analytics_context.template } : {} ),
          ...purchase_analytics_metadata,
        } );

        // The client supplies only the transaction anchor. C#Server verifies it
        // against the committed player-backend purchase before any destination
        // sees the event, so client price/currency are never optimization truth.
        const transaction_id = purchase.transaction_id;
        if ( transaction_id )
        {
          CustomEventsController.queueS2SEvent( 'purchase', {
            product_id: validated_purchase.purchase?.product_id ?? purchase.product_id ?? product_id,
            store: purchase.store,
          }, { transaction_id } );
        }
      }

      await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
        ...completed_purchase_context,
        purchaseid: validated_purchase.purchase?.id,
        purchase_status: validated_purchase.purchase?.status,
        entitlement_active: validated_purchase.entitlement?.active,
        google_play_consume_status: consume_status,
        recovered_owned_product,
        reused_owned_purchase,
      } );
      // The preceding validation is authoritative; only then may a purchase
      // based trigger be resolved. Dynamic import avoids a UI/service cycle.
      void import( './offer_trigger_controller.js' ).then( ({ OfferTriggerController }) =>
        OfferTriggerController.report( 'purchase_completed', { context: { offer_id: offer.id } } )
      );

      return validated_purchase;
    }
    catch ( e )
    {
      await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
        ...common_analytics_context,
        ...buildOfferPriceAnalyticsProperties( purchase ),
        ...getPurchaseFailureProperties( e, 'validation' ),
      } );
      throw e;
    }
  },

  /**
   * Stars have no client receipt: the server issues an invoice, Telegram's
   * sheet takes the payment, the bot webhook confirms it, and we poll the
   * order until the server has granted it.
   */
  async purchaseWithTelegramStars(
    offer: OfferDto,
    product_id: string,
    analytics_context: Record<string, unknown>,
    chain_step_id?: string,
    trigger_event_id?: string,
    placement_id?: string,
  ): Promise<ValidatePurchaseResponse>
  {
    let stage: 'store' | 'validation' = 'store';
    let order_id: string | undefined;
    try
    {
      const invoice = await createTelegramStarsInvoice( offer.id, {
        product_id,
        session_id: offer_session_id,
        ...( chain_step_id ? { chain_step_id } : {} ),
        ...( trigger_event_id ? { trigger_event_id } : {} ),
        ...( placement_id ? { placement_id } : {} ),
      } );
      order_id = invoice.order_id;
      if ( invoice.status !== 'pending' || !invoice.invoice_link ) throw new Error( `telegram_stars_order_${invoice.status}` );

      const sheet_status = await TelegramStars.openInvoice( invoice.invoice_link );
      if ( sheet_status === 'cancelled' ) throw new Error( 'telegram_stars_cancelled' );
      if ( sheet_status === 'failed' ) throw new Error( 'telegram_stars_payment_failed' );

      stage = 'validation';
      const order = await waitForStarsOrder( invoice.order_id );
      await this.refresh();

      const entitlement_active = entitlements_cache[offer.entitlement_key] === true
        && ( offer.purchase?.type === 'non_consumable' || offer.rewards?.no_ads === true );
      if ( entitlement_active ) persistEntitlement( offer.entitlement_key );
      const result: ValidatePurchaseResponse = {
        ok: true,
        ...( order.player ? { player: order.player } : {} ),
        entitlement: entitlement_active ? { key: offer.entitlement_key, active: true } : null,
        purchase: order.purchase ?? {
          id: 0, offer_id: offer.id, product_id: order.product_id, store: OfferStore.TelegramStars, status: 'fulfilled',
        },
        ...( order.granted ? { granted: order.granted } : {} ),
        grant_applied: true,
      };

      await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
        ...analytics_context,
        store: OfferStore.TelegramStars,
        currency: 'XTR',
        amount_stars: order.amount_stars,
        order_id: order.order_id,
        purchaseid: order.purchase?.id,
        purchase_status: order.purchase?.status,
        ...( order.purchase?.price_usd != null ? { priceusd: order.purchase.price_usd } : {} ),
        entitlement_active,
      } );
      void import( './offer_trigger_controller.js' ).then( ({ OfferTriggerController }) =>
        OfferTriggerController.report( 'purchase_completed', { context: { offer_id: offer.id } } )
      );
      return result;
    }
    catch ( e )
    {
      await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
        ...analytics_context,
        store: OfferStore.TelegramStars,
        ...( order_id ? { order_id } : {} ),
        ...getPurchaseFailureProperties( e, stage ),
      } );
      throw e;
    }
  },

  /**
   * TON / USDT through TON Connect. The server issues the order and the exact
   * transaction; the wallet signs it; the chain watcher on the server credits
   * the payment; we poll the order until it is granted. Leaving the sheet
   * never cancels anything: a background tracker finishes the purchase.
   */
  async purchaseWithTon(
    offer: OfferDto,
    product_id: string,
    analytics_context: Record<string, unknown>,
    chain_step_id?: string,
    trigger_event_id?: string,
    placement_id?: string,
  ): Promise<ValidatePurchaseResponse>
  {
    let stage: 'store' | 'validation' = 'store';
    let order_id: string | undefined;
    let asset: TonAsset | null = null;
    try
    {
      const prices = TonPayments.getPrices( product_id );
      if ( !prices ) throw new Error( 'ton_not_sold' );
      const choices: { asset: TonAsset; label: string }[] = [
        ...( prices.usdt !== undefined ? [ { asset: 'USDT' as const, label: TonPayments.formatAsset( 'USDT', prices.usdt ) } ] : [] ),
        ...( prices.ton !== undefined ? [ { asset: 'TON' as const, label: TonPayments.formatAsset( 'TON', prices.ton ) } ] : [] ),
      ];
      asset = await TonPaymentSheet.chooseAsset( choices, prices.usd !== undefined ? `$${prices.usd.toFixed( 2 )}` : undefined );
      if ( !asset ) throw new TonConnectFailure( 'cancelled_by_user' );

      TonPaymentSheet.show( { kind: 'connecting' } );
      TonPaymentSheet.onCancel( () => TonPayments.cancelPending() );
      const sender_address = await TonPayments.connect();
      const invoice = await createTonInvoice( offer.id, {
        asset,
        product_id,
        session_id: offer_session_id,
        sender_address,
        withdrawal_waiver: true,
        withdrawal_waiver_version: '1',
        ...( chain_step_id ? { chain_step_id } : {} ),
        ...( trigger_event_id ? { trigger_event_id } : {} ),
        ...( placement_id ? { placement_id } : {} ),
      } );
      order_id = invoice.order_id;
      // Cancel / × while the invoice was being created: the sheet is closed, nothing must reach the wallet.
      if ( !TonPaymentSheet.isOpen() ) throw new TonConnectFailure( 'cancelled_by_user' );
      let order: TonOrderDto = invoice;
      let transaction: TonSendTransactionRequest = invoice.transaction;

      // Sign; a stale request (the wallet took too long, the player switched wallets) is reissued, not failed.
      for ( let attempt = 0; ; attempt++ )
      {
        TonPaymentSheet.show( { kind: 'signing', amount: order.amount_display, valid_until_ms: transaction.validUntil * 1000, gas_note: asset === 'USDT' } );
        try
        {
          const boc = await TonPayments.sendTransaction( transaction );
          if ( boc ) await markTonOrderSent( order.order_id, boc ).catch( () => {} );
          break;
        }
        catch ( e )
        {
          const failure = classifyTonError( e );
          if ( failure.reason === 'tx_expired' && attempt < 2 )
          {
            const fresh = await getTonOrderTransaction( order.order_id, await TonPayments.connectedAddress() ).catch( ( reissue_error ) =>
            {
              // The order is no longer `pending` (the "expired" signature actually went through) or the re-issue
              // cap was hit: money may be on its way, so poll the order instead of failing.
              const code = reissue_error instanceof ApiError && reissue_error.body && typeof reissue_error.body === 'object' ? String( ( reissue_error.body as any ).error ?? '' ) : '';
              if ( code === 'order_not_payable' || code === 'too_many_reissues' ) return null;
              throw reissue_error;
            } );
            if ( !fresh ) break;
            order = fresh;
            transaction = fresh.transaction;
            continue;
          }
          throw failure;
        }
      }

      stage = 'validation';
      TonPaymentSheet.show( { kind: 'sent', amount: order.amount_display } );
      const final = await waitForTonOrder( order.order_id, {
        onUpdate: ( current ) =>
        {
          if ( !TonPaymentSheet.isOpen() ) { trackTonOrderInBackground( current.order_id ); return 'stop'; }
          tonSheetForOrder( current );
          // Part of the money arrived: no top-up from here. The sheet shows the order number for support;
          // the background tracker keeps watching in case the chain delivers the rest within the grace window.
          if ( current.status === 'underpaid' ) { trackTonOrderInBackground( current.order_id ); return 'stop'; }
          return 'continue';
        },
      } );
      void ackTonOrderOnce( final.order_id );
      await this.refresh();

      const entitlement_active = entitlements_cache[offer.entitlement_key] === true
        && ( offer.purchase?.type === 'non_consumable' || offer.rewards?.no_ads === true );
      if ( entitlement_active ) persistEntitlement( offer.entitlement_key );
      const result: ValidatePurchaseResponse = {
        ok: true,
        ...( final.player ? { player: final.player } : {} ),
        entitlement: entitlement_active ? { key: offer.entitlement_key, active: true } : null,
        purchase: final.purchase ?? {
          id: 0, offer_id: offer.id, product_id: final.product_id, store: OfferStore.Ton, status: 'fulfilled',
        },
        ...( final.granted ? { granted: final.granted } : {} ),
        grant_applied: true,
      };
      TonPaymentSheet.show( { kind: 'success' } );

      await trackOfferAnalytics( offer, OfferEventType.PurchaseSuccess, {
        ...analytics_context,
        store: OfferStore.Ton,
        currency: final.asset,
        ton_asset: final.asset,
        amount: final.amount,
        order_id: final.order_id,
        purchaseid: final.purchase?.id,
        purchase_status: final.purchase?.status,
        ...( final.purchase?.price_usd != null ? { priceusd: final.purchase.price_usd } : {} ),
        entitlement_active,
      } );
      void import( './offer_trigger_controller.js' ).then( ({ OfferTriggerController }) =>
        OfferTriggerController.report( 'purchase_completed', { context: { offer_id: offer.id } } )
      );
      return result;
    }
    catch ( e )
    {
      const message = e instanceof Error ? e.message : String( e );
      const pending = e instanceof TonPaymentPending || message === 'ton_payment_processing';
      if ( pending )
      {
        // Money may be on its way: not a failure. The tracker finishes it and the sheet already says so.
        if ( order_id ) trackTonOrderInBackground( order_id );
      }
      else
      {
        const failure = e instanceof TonConnectFailure ? e : null;
        const api_code = e instanceof ApiError && e.body && typeof e.body === 'object' ? String( ( e.body as any ).error ?? '' ) : '';
        if ( failure?.reason === 'cancelled_by_user' ) TonPaymentSheet.close();
        else if ( message === 'ton_order_expired_paid' && order_id ) TonPaymentSheet.show( { kind: 'needs_support', order_id } );
        else if ( message === 'ton_order_expired' || api_code === 'order_expired' || api_code === 'offer_expired' ) TonPaymentSheet.show( { kind: 'error', title: 'Order expired', detail: 'Nothing was charged. Open the offer again to get a new price request.', retry: false } );
        else if ( api_code === 'payment_in_progress' ) TonPaymentSheet.show( { kind: 'error', title: 'A payment is already on its way', detail: 'Your previous payment for this item is still being confirmed. It completes on its own.', retry: false } );
        else if ( api_code === 'too_many_invoices' ) TonPaymentSheet.show( { kind: 'error', title: 'Too many attempts', detail: 'Please wait a few minutes and try again. Nothing was charged.', retry: false } );
        else if ( api_code === 'ton_payments_disabled' || api_code === 'ton_merchant_not_ready' || api_code === 'usdt_disabled' ) TonPaymentSheet.show( { kind: 'error', title: 'Crypto checkout is unavailable right now', detail: 'Please try again later or use another payment method. Nothing was charged.', retry: false } );
        else if ( api_code === 'limit_reached' || api_code === 'order_too_large' ) TonPaymentSheet.show( { kind: 'error', title: 'Purchase limit reached', detail: 'This purchase exceeds the crypto spending limit. Nothing was charged.', retry: false } );
        else if ( e instanceof ApiError && e.status === 409 && e.body && typeof e.body === 'object' && ( e.body as any ).required )
        {
          const body = e.body as any;
          TonPaymentSheet.show( { kind: 'error', title: body.error === 'insufficient_ton_for_gas' ? 'Not enough TON for the fee' : 'Not enough funds', detail: `Needs ${body.required} ${body.asset}, the wallet has ${body.available} ${body.asset}.` } );
        }
        else if ( order_id && stage === 'validation' ) TonPaymentSheet.show( { kind: 'needs_support', order_id } );
        else TonPaymentSheet.show( { kind: 'error', title: 'Payment did not go through', detail: failure ? ( failure.reason === 'wrong_network' ? 'The wallet is on a different network.' : failure.reason === 'tx_expired' ? 'The request expired before it was confirmed.' : 'Nothing was charged.' ) : ( e instanceof ApiError ? String( e.message ) : 'Nothing was charged.' ) } );
        await trackOfferAnalytics( offer, OfferEventType.PurchaseFailed, {
          ...analytics_context,
          store: OfferStore.Ton,
          ...( asset ? { ton_asset: asset } : {} ),
          ...( order_id ? { order_id } : {} ),
          ...getPurchaseFailureProperties( e, stage ),
        } );
      }
      throw e;
    }
  },

  async restorePurchases(): Promise<void>
  {
    let restored = [];
    try
    {
      restored = await IapPlugin.restorePurchases();
    }
    catch ( e )
    {
      console.warn( '[Offers] restore failed', e );
      return;
    }

    if ( !restored.length ) return;

    for ( const purchase of restored )
    {
      const match = findRestoredOfferMatch( purchase.product_id );
      if ( !match ) continue;

      try
      {
        const validation = await validateOfferPurchase( match.offer.id, withOfferPurchaseMetadata(
          match.offer, purchase, match.product, match.chain_step_id, undefined, match.offer.placement?.id
        ) );
        const entitlement = validation.entitlement;
        await finishValidatedGooglePlayPurchase( match.offer, {
          ...purchase,
          purchase_type: validation.purchase?.purchase_type,
        } );
        if ( entitlement?.active )
        {
          persistEntitlement( entitlement.key );
          entitlements_cache[entitlement.key] = true;
          offers_cache = offers_cache.map( item =>
            item.entitlement_key === entitlement.key ? { ...item, owned: true } : item
          );
        }
      }
      catch ( e )
      {
        console.warn( '[Offers] restore validation failed', e );
      }
    }

    emitEntitlementsChanged();
  },
};
